import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  Gauge,
  Flame,
  HeartPulse,
  Inbox,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Moon,
  Plus,
  PlugZap,
  Send,
  Signature,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  Button,
  Dialog,
  Input,
  Label,
  Menu as AriaMenu,
  MenuItem,
  MenuTrigger,
  Popover,
  TextField,
} from "react-aria-components";
import { ConfirmDialog } from "@tellem/ui";
import { getCsrf } from "./api";
import type { CreateLeadBatchResponse } from "./components/LeadImportManager";
import { GlobalSearch, type SearchTarget } from "./components/GlobalSearch";
import { ProjectSwitcher } from "./components/ProjectSwitcher";
import { SignatureManager } from "./components/SignatureManager";
import { MailingHeaderSummary } from "./components/MailingHeaderSummary";
import { SendingDelaySettings } from "./components/SendingDelaySettings";
import { MailboxOnboardingWizard } from "./components/MailboxOnboardingWizard";

const AnalyticsPanel = lazy(() =>
  import("./components/AnalyticsPanel").then((module) => ({
    default: module.AnalyticsPanel,
  })),
);
const ExperimentCampaigns = lazy(() =>
  import("./components/ExperimentCampaigns").then((module) => ({
    default: module.ExperimentCampaigns,
  })),
);
const InboxManager = lazy(() =>
  import("./components/InboxManager").then((module) => ({
    default: module.InboxManager,
  })),
);
const LeadImportManager = lazy(() =>
  import("./components/LeadImportManager").then((module) => ({
    default: module.LeadImportManager,
  })),
);
const PlaybookManager = lazy(() =>
  import("./components/PlaybookManager").then((module) => ({
    default: module.PlaybookManager,
  })),
);
const IntegrationsPanel = lazy(() =>
  import("./components/IntegrationsPanel").then((module) => ({
    default: module.IntegrationsPanel,
  })),
);
const MonitoringPanel = lazy(() =>
  import("./components/MonitoringPanel").then((module) => ({
    default: module.MonitoringPanel,
  })),
);
const WarmupPanel = lazy(() =>
  import("./components/WarmupPanel").then((module) => ({
    default: module.WarmupPanel,
  })),
);

type Project = {
  id: string;
  name: string;
  slug: string;
  role: string;
  accent_color: string;
};
type Session = {
  user: { displayName: string; email: string };
  projects: Project[];
};
type Dashboard = {
  project: { id: string; name: string; slug: string };
  counts: Record<string, number>;
  openTrackingPolicy: { mode: "disabled"; ignoredRequests: number };
  navigation: {
    unreadReplies: number;
    pendingHumanReplies: number;
    deliveryErrors: number;
  };
  gmail: Array<{
    id: string;
    alias: string;
    email: string;
    provider: "gmail" | "gmail_app_password" | "lark";
    status: string;
    transportStatus:
      "unknown" | "ready" | "receive_only" | "unavailable" | "reauth_required";
    preferredTransport: string | null;
    lastTransportCheckAt: string | null;
    lastTransportErrorClass: string | null;
    dailyLimit: number;
    cooldownMinSeconds: number;
    cooldownMaxSeconds: number;
    sentToday: number;
    nextSlotAt: string | null;
  }>;
  activeBatch: null | {
    name: string;
    stage: string;
    progress: number;
    total: number;
  };
  activity: Array<{
    id: string;
    direction: "outbound" | "inbound";
    status: string;
    company: string;
    occurred_at: string;
  }>;
};

function mailTransportLabel(transport?: string | null) {
  if (transport === "gmail_api") return "Gmail API";
  if (transport === "smtp_starttls_587") return "SMTP 587";
  if (transport === "smtp_ssl_465") return "SMTP 465";
  return "транспорт";
}

function mailboxTransportStatus(account: Dashboard["gmail"][number]) {
  if (account.transportStatus === "ready")
    return `Готов · ${mailTransportLabel(account.preferredTransport)}`;
  if (account.transportStatus === "receive_only") return "Только входящие";
  if (account.transportStatus === "reauth_required") return "Нужен вход";
  if (account.transportStatus === "unavailable") return "Отправка недоступна";
  return "Нужна проверка";
}
const postJsonAction = (url: string, csrf: string, body: unknown = {}) =>
  fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json", "x-csrf-token": csrf },
    body: JSON.stringify(body),
  });

const nav = [
  ["Обзор", LayoutDashboard],
  ["Gmail", Mail],
  ["Прогрев", Flame],
  ["База лидов", Users],
  ["Гипотезы", Sparkles],
  ["Рассылки", Send],
  ["Входящие", Inbox],
  ["Аналитика", BarChart3],
  ["Мониторинг", HeartPulse],
  ["Интеграции", PlugZap],
] as const;
type View = (typeof nav)[number][0] | "Настройки";
const initialView = (): View => {
  const params = new URLSearchParams(window.location.search);
  if (params.has("gmail")) return "Gmail";
  const candidate = params.get("view");
  if (candidate === "Кампании") return "Рассылки";
  return nav.some(([label]) => label === candidate)
    ? (candidate as View)
    : "Обзор";
};

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [projectId, setProjectId] = useState("");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [dark, setDark] = useState(
    () => localStorage.getItem("theme") === "dark",
  );
  const [mobileNav, setMobileNav] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);
  const [mailingTabRequest, setMailingTabRequest] = useState(0);
  const [view, setView] = useState<View>(initialView);

  useEffect(() => {
    fetch("/api/auth/session")
      .then(async (response) =>
        response.ok ? (response.json() as Promise<Session>) : null,
      )
      .then((value) => {
        setSession(value);
        if (value?.projects[0]) {
          const requested = new URLSearchParams(window.location.search).get(
            "project",
          );
          setProjectId(
            value.projects.some((project) => project.id === requested)
              ? requested!
              : value.projects[0].id,
          );
        }
      })
      .catch(() => setSession(null));
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const pending = dashboard?.navigation.pendingHumanReplies ?? 0;
    document.title =
      pending > 0
        ? `(${pending > 99 ? "99+" : pending}) Tell’em`
        : "Tellem Outreach";
  }, [dashboard?.navigation?.pendingHumanReplies]);

  useEffect(() => {
    if (!projectId) return;
    let active = true;
    void fetch(`/api/projects/${projectId}/dashboard`)
      .then((response) => {
        if (!response.ok) throw new Error("DASHBOARD_UNAVAILABLE");
        return response.json() as Promise<Dashboard>;
      })
      .then((nextDashboard) => {
        if (!active) return;
        setDashboard(nextDashboard);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [projectId, dataVersion]);

  useEffect(() => {
    if (!projectId) return;
    let refreshTimer = 0;
    let version = Number(
      sessionStorage.getItem(`tellem:runtime-version:${projectId}`) ?? 0,
    );
    const channel = new BroadcastChannel(`tellem:runtime:${projectId}`);
    const events = new EventSource(
      `/api/projects/${projectId}/events/stream?after=${version}`,
    );
    const refresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(
        () => setDataVersion((current) => current + 1),
        150,
      );
    };
    const acceptVersion = (next: number, broadcast = true) => {
      if (!Number.isFinite(next) || next <= version) return;
      version = next;
      sessionStorage.setItem(
        `tellem:runtime-version:${projectId}`,
        String(next),
      );
      if (broadcast) channel.postMessage({ version: next });
      refresh();
    };
    const onProject = (event: Event) => {
      const message = event as MessageEvent;
      acceptVersion(Number(message.lastEventId || 0));
    };
    events.addEventListener("project", onProject);
    channel.addEventListener("message", (event) => {
      acceptVersion(Number(event.data?.version ?? 0), false);
    });
    const checkVersion = () => {
      if (document.hidden || !navigator.onLine) return;
      void fetch(`/api/projects/${projectId}/runtime/version`)
        .then((response) => {
          if (!response.ok) throw new Error("RUNTIME_VERSION_UNAVAILABLE");
          return response.json() as Promise<{ version?: number }>;
        })
        .then((payload) => acceptVersion(Number(payload.version ?? 0)))
        .catch(() => undefined);
    };
    const timer = window.setInterval(checkVersion, 15_000);
    const onVisibility = () => !document.hidden && refresh();
    const onOnline = () => checkVersion();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      events.close();
      channel.close();
      window.clearTimeout(refreshTimer);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    const params = new URLSearchParams();
    params.set("view", view);
    params.set("project", projectId);
    window.history.replaceState(null, "", `?${params.toString()}`);
  }, [view, projectId]);

  if (session === undefined)
    return (
      <div className="loader">
        <span />
      </div>
    );
  if (!session)
    return (
      <Login
        onLogin={(value) => {
          setSession(value);
          setProjectId(value.projects[0]?.id ?? "");
        }}
      />
    );

  const selected =
    session.projects.find((project) => project.id === projectId) ??
    session.projects[0];
  const navigate = (next: View) => {
    setView(next);
    setMobileNav(false);
    setProfileOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const createProject = async (name: string): Promise<string | null> => {
    const csrf = await getCsrf();
    if (!csrf) return "Войдите заново";
    const response = await fetch("/api/projects", {
      method: "POST",
      headers: { "content-type": "application/json", "x-csrf-token": csrf },
      body: JSON.stringify({ name }),
    });
    const result = (await response.json()) as {
      project?: Project;
      error?: string;
    };
    if (!response.ok || !result.project)
      return result.error === "PROJECT_LIMIT_REACHED"
        ? "Лимит проектов"
        : "Не удалось создать";
    setSession((current) =>
      current
        ? {
            ...current,
            projects: [...current.projects, result.project!].sort((a, b) =>
              a.name.localeCompare(b.name, "ru"),
            ),
          }
        : current,
    );
    setProjectId(result.project.id);
    navigate("Обзор");
    return null;
  };
  const updateProject = async (
    projectIdToUpdate: string,
    values: { name: string; slug: string },
  ): Promise<string | null> => {
    const csrf = await getCsrf();
    if (!csrf) return "Войдите заново";
    const response = await fetch(`/api/projects/${projectIdToUpdate}`, {
      method: "PATCH",
      headers: { "content-type": "application/json", "x-csrf-token": csrf },
      body: JSON.stringify(values),
    });
    const result = (await response.json()) as {
      project?: Pick<Project, "id" | "name" | "slug" | "accent_color">;
      error?: string;
    };
    if (!response.ok || !result.project)
      return result.error === "PROJECT_SLUG_CONFLICT"
        ? "Этот код уже занят"
        : result.error === "PERMISSION_DENIED"
          ? "Нет доступа"
          : "Не удалось сохранить";
    setSession((current) =>
      current
        ? {
            ...current,
            projects: current.projects
              .map((project) =>
                project.id === projectIdToUpdate
                  ? { ...project, ...result.project }
                  : project,
              )
              .sort((a, b) => a.name.localeCompare(b.name, "ru")),
          }
        : current,
    );
    setDataVersion((current) => current + 1);
    return null;
  };
  const logout = async () => {
    const csrf = await getCsrf();
    if (csrf) await postJsonAction("/api/auth/logout", csrf).catch(() => null);
    sessionStorage.removeItem("csrf");
    setProfileOpen(false);
    setDashboard(null);
    setProjectId("");
    setSession(null);
  };
  const openSearchTarget = (target: SearchTarget) => {
    if (target.projectId && target.projectId !== projectId)
      setProjectId(target.projectId);
    if (
      nav.some(([label]) => label === target.view) ||
      target.view === "Настройки"
    )
      navigate(target.view as View);
  };
  return (
    <div className="app-shell">
      <aside className={mobileNav ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <div className="brand-mark">T</div>
          <div>
            <strong>Tellem</strong>
            <span>Outreach hub</span>
          </div>
        </div>
        <button
          className="mobile-close"
          onClick={() => setMobileNav(false)}
          aria-label="Закрыть меню"
        >
          <X size={20} />
        </button>
        <nav aria-label="Основная навигация">
          {nav.map(([label, Icon]) => {
            const pending = dashboard?.navigation.pendingHumanReplies ?? 0;
            const unread = dashboard?.navigation.unreadReplies ?? 0;
            return (
              <button
                key={label}
                className={view === label ? "nav-item active" : "nav-item"}
                aria-current={view === label ? "page" : undefined}
                onClick={() => navigate(label)}
              >
                <Icon size={18} />
                <span>{label}</span>
                {label === "Входящие" && pending > 0 && (
                  <em
                    aria-label={`Требуют ответа: ${pending}. Новых: ${unread}`}
                    title={`Новых: ${unread}\nТребуют ответа: ${pending}`}
                  >
                    {pending > 99 ? "99+ ждут" : `${pending} ждут`}
                  </em>
                )}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <div
            className="profile-wrap"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node))
                setProfileOpen(false);
            }}
          >
            {profileOpen && (
              <div className="profile-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => navigate("Настройки")}
                >
                  <Settings size={16} />
                  <span>Настройки</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="danger"
                  onClick={() => void logout()}
                >
                  <LogOut size={16} />
                  <span>Выйти</span>
                </button>
              </div>
            )}
            <button
              type="button"
              className={
                view === "Настройки"
                  ? "profile-trigger active"
                  : "profile-trigger"
              }
              aria-expanded={profileOpen}
              aria-haspopup="menu"
              onClick={() => setProfileOpen((value) => !value)}
            >
              <div className="avatar">
                {session.user.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <strong>{session.user.displayName}</strong>
                <span>{selected?.role ?? "viewer"}</span>
              </div>
              <ChevronUp size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main>
        <header className="topbar">
          <Button
            className="icon-button menu-button"
            onPress={() => setMobileNav(true)}
            aria-label="Открыть меню"
          >
            <Menu size={20} />
          </Button>
          <div className="project-select-wrap">
            <ProjectSwitcher
              projects={session.projects}
              value={projectId}
              onChange={(next) => {
                setProjectId(next);
                setDataVersion((current) => current + 1);
              }}
              onCreate={createProject}
              onUpdate={updateProject}
            />
          </div>
          <div className="top-actions">
            <MailingHeaderSummary
              projectId={projectId}
              refreshKey={dataVersion}
              onOpen={() => {
                setMailingTabRequest((current) => current + 1);
                navigate("Рассылки");
              }}
            />
            <GlobalSearch
              projectId={projectId}
              projects={session.projects}
              onSelect={openSearchTarget}
            />
            <Button
              className="icon-button"
              onPress={() => setDark((value) => !value)}
              aria-label="Сменить тему"
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button
              className="primary-button"
              onPress={() => navigate("Рассылки")}
            >
              <Plus size={17} /> Новая рассылка
            </Button>
          </div>
        </header>

        <div className="content">
          {view === "Обзор" ? (
            <>
              <Metrics dashboard={dashboard} />
              <div className="dashboard-grid">
                <Funnel dashboard={dashboard} />
                <BatchCard
                  dashboard={dashboard}
                  onOpen={() => navigate("Рассылки")}
                />
                <InboxCard
                  dashboard={dashboard}
                  onOpen={() => navigate("Входящие")}
                />
              </div>
            </>
          ) : (
            <Suspense
              fallback={
                <article className="card section-loading" aria-live="polite">
                  <div className="empty compact">
                    <strong>Загрузка…</strong>
                  </div>
                </article>
              }
            >
              <SectionPage
                key={selected?.id}
                view={view}
                selected={selected}
                dashboard={dashboard}
                refreshKey={dataVersion}
                mailingTabRequest={mailingTabRequest}
                onNavigate={navigate}
                onDataChange={() => setDataVersion((value) => value + 1)}
              />
            </Suspense>
          )}
        </div>
      </main>
    </div>
  );
}

function Login({ onLogin }: { onLogin: (session: Session) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) return setError("Проверьте почту и пароль");
    const { csrf } = (await response.json()) as { csrf: string };
    sessionStorage.setItem("csrf", csrf);
    const sessionResponse = await fetch("/api/auth/session");
    onLogin((await sessionResponse.json()) as Session);
  };
  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="brand login-brand">
          <div className="brand-mark">T</div>
          <div>
            <strong>Tellem</strong>
            <span>Outreach hub</span>
          </div>
        </div>
        <div>
          <p className="eyebrow">Панель команды</p>
          <h1>Войти</h1>
          <p>Рассылки, ответы и данные проектов.</p>
        </div>
        <form onSubmit={submit}>
          <TextField value={email} onChange={setEmail} type="email" isRequired>
            <Label>Почта</Label>
            <Input placeholder="name@company.ru" />
          </TextField>
          <TextField
            value={password}
            onChange={setPassword}
            type="password"
            isRequired
          >
            <Label>Пароль</Label>
            <Input placeholder="••••••••••••" />
          </TextField>
          {error && <p className="form-error">{error}</p>}
          <Button type="submit" className="primary-button login-button">
            Войти
          </Button>
        </form>
        <p className="security-note">
          <ShieldCheck size={16} /> Доступ только по приглашению
        </p>
      </section>
      <aside className="login-visual">
        <div>
          <span>Почта</span>
          <strong>OAuth</strong>
          <p>без паролей Gmail</p>
        </div>
        <div>
          <span>Отправка</span>
          <strong>Ручная</strong>
          <p>после подтверждения</p>
        </div>
      </aside>
    </main>
  );
}

function Metrics({ dashboard }: { dashboard: Dashboard | null }) {
  const items = useMemo(
    () =>
      [
        ["Лиды", dashboard?.counts.leads ?? 0, Users, "база проекта"],
        ["Отправлено", dashboard?.counts.sent ?? 0, Send, "за 30 дней"],
        [
          "Ответы людей",
          dashboard?.counts.human_replies ?? 0,
          Inbox,
          "только люди",
        ],
        [
          "Содержательные",
          dashboard?.counts.substantive_replies ?? 0,
          Inbox,
          "по смыслу",
        ],
        ["Встречи", dashboard?.counts.meetings ?? 0, Gauge, "главный сигнал"],
      ] as const,
    [dashboard],
  );
  return (
    <section className="metrics">
      {items.map(([label, value, Icon, note]) => (
        <article key={label}>
          <div className="metric-icon">
            <Icon size={19} />
          </div>
          <div>
            <span>{label}</span>
            <strong>{value.toLocaleString("ru-RU")}</strong>
            <small>{note}</small>
          </div>
        </article>
      ))}
    </section>
  );
}

function Funnel({ dashboard }: { dashboard: Dashboard | null }) {
  const sent = dashboard?.counts.sent ?? 0;
  const delivered = dashboard?.counts.presumed_delivered ?? 0;
  const replies = dashboard?.counts.human_replies ?? 0;
  const substantive = dashboard?.counts.substantive_replies ?? 0;
  const leads = dashboard?.counts.lead_signals ?? 0;
  const meetings = dashboard?.counts.meetings ?? 0;
  const customerDevelopment = dashboard?.counts.customer_development ?? 0;
  const demos = dashboard?.counts.demos ?? 0;
  const pilots = dashboard?.counts.pilots ?? 0;
  const maximum = Math.max(
    sent,
    delivered,
    replies,
    substantive,
    leads,
    meetings,
    customerDevelopment,
    demos,
    pilots,
    1,
  );
  return (
    <article className="card funnel-card">
      <CardTitle title="Воронка" note="Только реальные события" />
      {sent || replies || meetings ? (
        <div className="funnel-bars">
          {[
            ["Отправлено", sent],
            ["Доставлено, оценочно", delivered],
            ["Ответы людей", replies],
            ["Содержательные", substantive],
            ["Лиды", leads],
            ["Встречи", meetings],
            ["Customer Development", customerDevelopment],
            ["Демо", demos],
            ["Пилоты", pilots],
          ].map(([label, value]) => (
            <div className="funnel-row" key={String(label)}>
              <span>{label}</span>
              <div>
                <i style={{ width: `${(Number(value) / maximum) * 100}%` }} />
              </div>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">
          <BarChart3 size={25} />
          <strong>Недостаточно данных</strong>
          <span>Воронка появится после тестовой отправки.</span>
        </div>
      )}
    </article>
  );
}

function BatchCard({
  dashboard,
  onOpen,
}: {
  dashboard: Dashboard | null;
  onOpen: () => void;
}) {
  const batch = dashboard?.activeBatch;
  const progress = batch?.total
    ? Math.round((batch.progress / batch.total) * 100)
    : 0;
  return (
    <article className="card batch-card">
      <CardTitle
        title="Активный батч"
        note={batch ? batch.stage : "Нет запуска"}
      />
      {batch ? (
        <>
          <h3>{batch.name}</h3>
          <div className="progress-meta">
            <span>
              {batch.progress} из {batch.total}
            </span>
            <strong>{progress}%</strong>
          </div>
          <div className="progress">
            <i style={{ width: `${progress}%` }} />
          </div>
          <div className="batch-actions">
            <Button className="secondary-button" onPress={onOpen}>
              Открыть
            </Button>
            <Button className="ghost-button" isDisabled>
              Пауза
            </Button>
          </div>
        </>
      ) : (
        <div className="empty">
          <Send size={25} />
          <strong>Нет активной отправки</strong>
          <span>Создайте батч после проверки playbook.</span>
        </div>
      )}
    </article>
  );
}

function InboxCard({
  dashboard,
  onOpen,
}: {
  dashboard: Dashboard | null;
  onOpen: () => void;
}) {
  const replies = dashboard?.counts.human_replies ?? 0;
  const activity = dashboard?.activity ?? [];
  const eventLabel = (direction: "outbound" | "inbound", status: string) => {
    if (direction === "inbound") return "Получено";
    if (status === "sent") return "Отправлено";
    if (status === "bounced") return "Баунс";
    if (status === "failed") return "Ошибка";
    return "В очереди";
  };
  return (
    <article className="card inbox-card">
      <CardTitle title="Поток писем" note={`${replies} ответов`} />
      {activity.length ? (
        <div className="live-activity-list">
          {activity.slice(0, 6).map((item) => (
            <div key={item.id}>
              <span className={item.direction}>
                {item.direction === "inbound" ? (
                  <Inbox size={14} />
                ) : (
                  <Send size={14} />
                )}
              </span>
              <div>
                <strong>{item.company}</strong>
                <small>{eventLabel(item.direction, item.status)}</small>
              </div>
              <time>
                {new Date(item.occurred_at).toLocaleTimeString("ru-RU", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </time>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty compact">
          <Inbox size={25} />
          <strong>Писем пока нет</strong>
        </div>
      )}
      <Button className="text-button" onPress={onOpen}>
        Все входящие →
      </Button>
    </article>
  );
}

function SectionPage({
  view,
  selected,
  dashboard,
  refreshKey,
  mailingTabRequest,
  onNavigate,
  onDataChange,
}: {
  view: View;
  selected: Project | undefined;
  dashboard: Dashboard | null;
  refreshKey: number;
  mailingTabRequest: number;
  onNavigate: (view: View) => void;
  onDataChange: () => void;
}) {
  const [pendingLeadBatch, setPendingLeadBatch] =
    useState<CreateLeadBatchResponse | null>(null);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [gmailMessage, setGmailMessage] = useState("");
  const [larkOpen, setLarkOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [googlePasswordOpen, setGooglePasswordOpen] = useState(false);
  const [bulkOnboardingOpen, setBulkOnboardingOpen] = useState(false);
  const [googlePasswordEmail, setGooglePasswordEmail] = useState("");
  const [googleAppPassword, setGoogleAppPassword] = useState("");
  const [larkEmail, setLarkEmail] = useState("");
  const [larkCode, setLarkCode] = useState("");
  const signatureTriggerRef = useRef<HTMLButtonElement>(null);
  const providerTriggerRef = useRef<HTMLButtonElement>(null);
  const accounts = dashboard?.gmail ?? [];
  const mailboxGroups = [
    {
      key: "google",
      title: "Google",
      accounts: accounts.filter((account) => account.provider !== "lark"),
    },
    {
      key: "lark",
      title: "Lark",
      accounts: accounts.filter((account) => account.provider === "lark"),
    },
  ].filter((group) => group.accounts.length > 0);
  const connectGmail = async () => {
    if (!selected || gmailBusy) return;
    setGmailBusy(true);
    setGmailMessage("");
    try {
      const csrf = await getCsrf();
      if (!csrf) {
        setGmailMessage("Войдите заново для подключения.");
        return;
      }
      const response = await postJsonAction(
        `/api/projects/${selected.id}/gmail/oauth/start`,
        csrf,
      );
      const result = (await response.json()) as {
        authorizationUrl?: string;
        error?: string;
      };
      if (response.ok && result.authorizationUrl) {
        window.location.assign(result.authorizationUrl);
        return;
      }
      setGmailMessage(
        result.error === "GOOGLE_OAUTH_NOT_CONFIGURED"
          ? "Сначала нужны Google Client ID и Client Secret."
          : "Не удалось открыть вход Google.",
      );
    } catch {
      setGmailMessage("Нет связи с API.");
    } finally {
      setGmailBusy(false);
    }
  };
  const disconnectGmail = async (accountId: string) => {
    if (!selected || gmailBusy) return;
    setGmailBusy(true);
    setGmailMessage("");
    try {
      const csrf = await getCsrf();
      if (!csrf) return setGmailMessage("Войдите заново.");
      const response = await fetch(
        `/api/projects/${selected.id}/gmail/${accountId}`,
        { method: "DELETE", headers: { "x-csrf-token": csrf } },
      );
      if (!response.ok) return setGmailMessage("Не удалось отключить ящик.");
      setGmailMessage("Ящик отключён.");
      onDataChange();
    } catch {
      setGmailMessage("Нет связи с API.");
    } finally {
      setGmailBusy(false);
    }
  };
  const connectLark = async () => {
    if (!selected || gmailBusy) return;
    setGmailBusy(true);
    setGmailMessage("");
    try {
      const csrf = await getCsrf();
      if (!csrf) return setGmailMessage("Войдите заново.");
      const response = await postJsonAction(
        `/api/projects/${selected.id}/mailboxes/lark`,
        csrf,
        { email: larkEmail.trim(), authorizationCode: larkCode.trim() },
      );
      const result = (await response.json()) as { error?: string };
      if (!response.ok) {
        setGmailMessage(
          result.error === "LARK_CREDENTIALS_INVALID"
            ? "Проверьте почту и код входа."
            : "Lark не принял данные.",
        );
        return;
      }
      setLarkCode("");
      setLarkEmail("");
      setLarkOpen(false);
      setGmailMessage("");
      onDataChange();
    } catch {
      setGmailMessage("Нет связи с API.");
    } finally {
      setGmailBusy(false);
    }
  };
  const connectGoogleAppPassword = async () => {
    if (!selected || gmailBusy) return;
    setGmailBusy(true);
    setGmailMessage("");
    try {
      const csrf = await getCsrf();
      if (!csrf) return setGmailMessage("Войдите заново.");
      const response = await postJsonAction(
        `/api/projects/${selected.id}/mailboxes/google-app-password`,
        csrf,
        { email: googlePasswordEmail.trim(), appPassword: googleAppPassword },
      );
      const result = (await response.json()) as {
        error?: string;
        pendingVerification?: boolean;
      };
      if (!response.ok) {
        setGmailMessage(
          result.error === "GMAIL_APP_PASSWORD_INVALID"
            ? "Нужен 16-значный пароль приложения."
            : "Google не принял пароль приложения.",
        );
        return;
      }
      setGoogleAppPassword("");
      setGooglePasswordEmail("");
      setGooglePasswordOpen(false);
      setGmailMessage(result.pendingVerification ? "Отправка недоступна." : "");
      onDataChange();
    } catch {
      setGmailMessage("Нет связи с API.");
    } finally {
      setGmailBusy(false);
    }
  };
  return (
    <section className="section-page">
      {view === "Gmail" && (
        <div className="gmail-page">
          <div className="mail-add-row">
            <div className="mail-tool-anchor">
              <Button
                ref={signatureTriggerRef}
                className="icon-button signature-toggle"
                aria-label="Подписи"
                aria-pressed={signatureOpen}
                onPress={() => {
                  setSignatureOpen((open) => !open);
                  setGooglePasswordOpen(false);
                  setLarkOpen(false);
                }}
              >
                <Signature size={18} aria-hidden="true" />
              </Button>
              {selected && signatureOpen && (
                <Popover
                  isOpen={signatureOpen}
                  onOpenChange={setSignatureOpen}
                  triggerRef={signatureTriggerRef}
                  placement="bottom end"
                  className="signature-inline-popover"
                >
                  <Dialog className="mail-popover-dialog">
                    <SignatureManager
                      projectId={selected.id}
                      accounts={accounts}
                      compact
                    />
                  </Dialog>
                </Popover>
              )}
            </div>
            <div className="mail-tool-anchor">
              <MenuTrigger>
                <Button
                  ref={providerTriggerRef}
                  className="primary-button mail-provider-trigger"
                  isDisabled={gmailBusy}
                >
                  <Plus size={17} aria-hidden="true" />
                  Добавить почту
                  <ChevronDown size={15} aria-hidden="true" />
                </Button>
                <Popover
                  className="mail-provider-popover"
                  placement="bottom end"
                >
                  <AriaMenu
                    className="mail-provider-menu"
                    aria-label="Почтовый сервис"
                    onAction={(key) => {
                      setSignatureOpen(false);
                      setGmailMessage("");
                      if (key === "google") {
                        void connectGmail();
                        return;
                      }
                      if (key === "bulk") {
                        setGooglePasswordOpen(false);
                        setLarkOpen(false);
                        setBulkOnboardingOpen(true);
                        return;
                      }
                      if (key === "google-workspace") {
                        setLarkOpen(false);
                        setGooglePasswordOpen(true);
                        return;
                      }
                      setGooglePasswordOpen(false);
                      setLarkOpen(true);
                    }}
                  >
                    <MenuItem
                      id="bulk"
                      className="mail-provider-option"
                      textValue="Массовое подключение"
                    >
                      <span className="mail-provider-option-mark bulk-mark">
                        <Users size={18} />
                      </span>
                      <span>
                        <strong>Несколько ящиков</strong>
                        <small>Google Workspace или Lark</small>
                      </span>
                    </MenuItem>
                    <MenuItem
                      id="google"
                      className="mail-provider-option"
                      textValue="Google"
                    >
                      <span className="mail-provider-option-mark google-mark">
                        G
                      </span>
                      <span>
                        <strong>Google</strong>
                        <small>Вход через OAuth</small>
                      </span>
                    </MenuItem>
                    <MenuItem
                      id="google-workspace"
                      className="mail-provider-option"
                      textValue="Google Workspace"
                    >
                      <span className="mail-provider-option-mark google-mark">
                        G
                      </span>
                      <span>
                        <strong>Google Workspace</strong>
                        <small>Пароль приложения</small>
                      </span>
                    </MenuItem>
                    <MenuItem
                      id="lark"
                      className="mail-provider-option"
                      textValue="Lark Mail"
                    >
                      <span className="mail-provider-option-mark lark-mark">
                        <Mail size={18} />
                      </span>
                      <span>
                        <strong>Lark Mail</strong>
                        <small>IMAP и SMTP</small>
                      </span>
                    </MenuItem>
                  </AriaMenu>
                </Popover>
              </MenuTrigger>
              {(googlePasswordOpen || larkOpen) && (
                <Popover
                  isOpen
                  onOpenChange={(open) => {
                    if (!open) {
                      setGooglePasswordOpen(false);
                      setLarkOpen(false);
                    }
                  }}
                  triggerRef={providerTriggerRef}
                  placement="bottom end"
                  className="mail-connect-popover"
                >
                  <Dialog className="mail-popover-dialog">
                    {googlePasswordOpen ? (
                      <article className="lark-connect-form">
                        <header>
                          <div>
                            <h2>Google без Authenticator</h2>
                            <p>
                              Обычный пароль не нужен. Код хранится
                              зашифрованно.
                            </p>
                          </div>
                          <Button
                            className="icon-button"
                            aria-label="Закрыть"
                            onPress={() => setGooglePasswordOpen(false)}
                          >
                            <X size={17} />
                          </Button>
                        </header>
                        <div className="lark-fields">
                          <TextField
                            className="field-group"
                            value={googlePasswordEmail}
                            onChange={setGooglePasswordEmail}
                          >
                            <Label>Почта Google Workspace</Label>
                            <Input
                              type="email"
                              placeholder="name@company.ru"
                              autoComplete="email"
                            />
                          </TextField>
                          <TextField
                            className="field-group"
                            value={googleAppPassword}
                            onChange={setGoogleAppPassword}
                          >
                            <Label>Пароль приложения</Label>
                            <Input
                              type="password"
                              placeholder="xxxx xxxx xxxx xxxx"
                              autoComplete="new-password"
                            />
                          </TextField>
                        </div>
                        <footer>
                          <a
                            href="https://myaccount.google.com/apppasswords"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Пароли приложений
                          </a>
                          <Button
                            className="primary-button"
                            onPress={connectGoogleAppPassword}
                            isDisabled={
                              gmailBusy ||
                              !googlePasswordEmail.trim() ||
                              !googleAppPassword.trim()
                            }
                          >
                            {gmailBusy ? "Проверяем…" : "Подключить"}
                          </Button>
                        </footer>
                      </article>
                    ) : (
                      <article className="lark-connect-form">
                        <header>
                          <div>
                            <h2>Подключить Lark</h2>
                            <p>
                              Нужен код входа для сторонней почты, не пароль.
                            </p>
                          </div>
                          <Button
                            className="icon-button"
                            aria-label="Закрыть"
                            onPress={() => setLarkOpen(false)}
                          >
                            <X size={17} />
                          </Button>
                        </header>
                        <div className="lark-fields">
                          <TextField
                            className="field-group"
                            value={larkEmail}
                            onChange={setLarkEmail}
                          >
                            <Label>Почта Lark</Label>
                            <Input
                              type="email"
                              placeholder="name@company.com"
                              autoComplete="email"
                            />
                          </TextField>
                          <TextField
                            className="field-group"
                            value={larkCode}
                            onChange={setLarkCode}
                          >
                            <Label>Код входа</Label>
                            <Input
                              type="password"
                              placeholder="Код из Lark"
                              autoComplete="new-password"
                            />
                          </TextField>
                        </div>
                        <footer>
                          <a
                            href="https://www.larksuite.com/hc/en-US/articles/308017167834-obtain-the-login-authorization-code-for-third-party-emails"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Где взять код
                          </a>
                          <Button
                            className="primary-button"
                            onPress={connectLark}
                            isDisabled={
                              gmailBusy || !larkEmail.trim() || !larkCode.trim()
                            }
                          >
                            {gmailBusy ? "Проверяем…" : "Подключить"}
                          </Button>
                        </footer>
                      </article>
                    )}
                  </Dialog>
                </Popover>
              )}
            </div>
          </div>
          {gmailMessage && !googlePasswordOpen && !larkOpen && (
            <p className="mail-connect-status" role="status">
              {gmailMessage}
            </p>
          )}
          {accounts.length > 0 && (
            <div
              className="mailbox-provider-groups"
              aria-label="Подключённые почтовые ящики"
            >
              {mailboxGroups.map((group) => (
                <section className="mailbox-provider-group" key={group.key}>
                  <header>
                    <h2>{group.title}</h2>
                    <span>{group.accounts.length}</span>
                  </header>
                  <div className="mailbox-card-grid">
                    {group.accounts.map((account) => {
                      const status = mailboxTransportStatus(account);
                      return (
                        <article className="mailbox-card" key={account.email}>
                          <div className="mailbox-card-copy">
                            <span
                              className={`status-dot ${account.transportStatus === "ready" ? "healthy" : "reauth"}`}
                              aria-hidden="true"
                            />
                            <div>
                              <strong>{account.email}</strong>
                              <small>{status}</small>
                            </div>
                          </div>
                          <div className="mailbox-card-actions">
                            <ConfirmDialog
                              trigger={
                                <Button
                                  className="mailbox-remove"
                                  aria-label={`Удалить ${account.email}`}
                                  isDisabled={gmailBusy}
                                >
                                  <Trash2 size={15} />
                                </Button>
                              }
                              title="Удалить ящик"
                              confirmLabel="Удалить"
                              onConfirm={() => disconnectGmail(account.id)}
                            >
                              Рассылки с этого ящика остановятся. История писем
                              сохранится.
                            </ConfirmDialog>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
          {selected && (
            <MailboxOnboardingWizard
              projectId={selected.id}
              isOpen={bulkOnboardingOpen}
              onOpenChange={setBulkOnboardingOpen}
              onConnected={onDataChange}
              onOpenWarmup={() => onNavigate("Прогрев")}
            />
          )}
        </div>
      )}
      {view === "Прогрев" && selected && (
        <WarmupPanel projectId={selected.id} />
      )}
      {view === "База лидов" && selected && (
        <LeadImportManager
          projectId={selected.id}
          onChange={onDataChange}
          onBatchCreated={(batch) => {
            setPendingLeadBatch(batch);
            onNavigate("Рассылки");
          }}
        />
      )}
      {view === "Гипотезы" && selected && (
        <PlaybookManager projectId={selected.id} />
      )}
      {view === "Рассылки" && selected && (
        <ExperimentCampaigns
          projectId={selected.id}
          projectName={selected.name}
          senderMailboxes={accounts
            .filter(
              (account) =>
                account.status === "healthy" &&
                account.transportStatus === "ready",
            )
            .map((account) => ({
              id: account.id,
              email: account.email,
              provider: account.provider,
              dailyLimit: account.dailyLimit,
              sentToday: account.sentToday,
              nextSlotAt: account.nextSlotAt,
            }))}
          leadCount={dashboard?.counts.leads ?? 0}
          refreshKey={refreshKey}
          activeTabRequest={mailingTabRequest}
          initialLeadBatchId={pendingLeadBatch?.id ?? ""}
          onChange={onDataChange}
        />
      )}
      {view === "Входящие" && selected && (
        <InboxManager
          projectId={selected.id}
          refreshKey={refreshKey}
          onChange={onDataChange}
        />
      )}
      {view === "Аналитика" && selected && (
        <AnalyticsPanel projectId={selected.id} refreshKey={refreshKey} />
      )}
      {view === "Мониторинг" && selected && (
        <MonitoringPanel projectId={selected.id} refreshKey={refreshKey} />
      )}
      {view === "Интеграции" && selected && (
        <IntegrationsPanel projectId={selected.id} onChange={onDataChange} />
      )}
      {view === "Настройки" && (
        <div className="section-grid">
          {selected && dashboard && (
            <SendingDelaySettings
              projectId={selected.id}
              accounts={dashboard.gmail.map((account) => ({
                id: account.id,
                email: account.email,
                cooldownMinSeconds: account.cooldownMinSeconds,
                cooldownMaxSeconds: account.cooldownMaxSeconds,
              }))}
              onChange={onDataChange}
            />
          )}
          <InfoCard
            title="Проект"
            text={
              selected ? `${selected.name} · ${selected.slug}` : "Не выбран"
            }
          />
          <InfoCard title="Роль" text={selected?.role ?? "viewer"} />
          <Button
            className="secondary-button section-action"
            onPress={() => onNavigate("Интеграции")}
          >
            Интеграции
          </Button>
        </div>
      )}
    </section>
  );
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <article className="section-card info-card">
      <h2>{title}</h2>
      <p>{text}</p>
    </article>
  );
}
function CardTitle({ title, note }: { title: string; note: string }) {
  return (
    <div className="card-title">
      <h2>{title}</h2>
      <span>{note}</span>
    </div>
  );
}
